import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { MasterCompilation } from '../api/types';
import { SynthesisTable } from '../components/SynthesisTable';
import { CompiledSynthesisChart } from '../components/CompiledSynthesisChart';
import { CompiledExportToolbar } from '../components/CompiledExportToolbar';
import { DateRangeFilter } from '../components/DateRangeFilter';

interface Range {
  start: string;
  end: string;
}

/**
 * Compilation "Master" d'un dossier : contrairement à SynthesisPage/WorkloadPage
 * (une carte par diagramme source, chacune avec son propre export), ici les
 * tâches cochées "Master" (voir TaskTable) de tous les diagrammes du dossier
 * sont fusionnées en UN SEUL tableau et UN SEUL diagramme compilé — demande
 * explicite de l'utilisateur. L'export PDF (CompiledExportToolbar, plage de
 * dates paramétrable) porte donc sur ce compilé, visible dans les deux vues
 * puisqu'il n'y a pas d'autre export ici. `DateRangeFilter` limite en plus
 * les tâches AFFICHÉES à celles chevauchant une plage — sans fenêtre par
 * défaut (voir FoldersService.getMaster), contrairement à Charge de travail.
 */
export function MasterPage() {
  const { t } = useTranslation();
  const { folderId } = useParams();
  const [data, setData] = useState<MasterCompilation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');
  const [appliedRange, setAppliedRange] = useState<Range | null>(null);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');

  const load = () => {
    if (!folderId) return;
    setRefreshing(true);
    setError(null);
    const query = appliedRange ? `?start=${appliedRange.start}&end=${appliedRange.end}` : '';
    api
      .get<MasterCompilation>(`/folders/${folderId}/master${query}`)
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setRefreshing(false));
  };

  useEffect(load, [folderId, appliedRange]);

  if (error) return <p className="error-text">{error}</p>;
  if (!data) return <p>{t('common.loading')}</p>;

  const rangeInvalid = !startInput || !endInput || startInput > endInput;
  const title = `${t('master.title')} · ${data.folderName}`;

  return (
    <div>
      <Link to={`/folders/${folderId}`}>&larr; {t('diagram.backToFolder')}</Link>

      <div className="card diagram-toolbar" style={{ marginTop: 16, marginBottom: 16 }}>
        <strong className="diagram-name-display">{title}</strong>
        <button className="btn icon-btn" onClick={load} disabled={refreshing} title={t('synthesis.refresh') as string}>
          🔄
        </button>
      </div>

      <DateRangeFilter
        startInput={startInput}
        endInput={endInput}
        onStartInputChange={setStartInput}
        onEndInputChange={setEndInput}
        onApply={() => setAppliedRange({ start: startInput, end: endInput })}
        onReset={appliedRange ? () => setAppliedRange(null) : null}
        applyDisabled={rangeInvalid}
      />

      <div className="toolbar" style={{ marginBottom: 8 }}>
        <button className={`btn ${view === 'table' ? 'btn-primary' : ''}`} onClick={() => setView('table')}>
          {t('diagram.viewTable')}
        </button>
        <button className={`btn ${view === 'chart' ? 'btn-primary' : ''}`} onClick={() => setView('chart')}>
          {t('diagram.viewChart')}
        </button>
      </div>

      <CompiledExportToolbar groups={data.groups} title={title} />

      {!appliedRange && data.groups.every((g) => g.tasks.length === 0) ? (
        <p className="synthesis-empty">{t('master.empty')}</p>
      ) : view === 'table' ? (
        <SynthesisTable groups={data.groups} />
      ) : (
        <CompiledSynthesisChart groups={data.groups} />
      )}
    </div>
  );
}
